import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { ChatTextarea, Stack } from '@optimus-web/core';
import { lt, useFeatureFlag } from '@mews/framework';
import { FeatureFlag } from '@mews-commander/enums';
import { Message } from '@mews-commander/messages';
import { useCurrentProfileId } from '@mews-commander/core';
import { useCommanderSelector } from '@mews-commander/utils';
import { SendMessageProps } from '../containers/SendMessage';
import { currentThreadMessages } from '../selectors';
import { MessengerFooter } from './style';
import { AiSuggestions } from './AiSuggestions/AiSuggestions';

export interface SendMessageComponentProps extends SendMessageProps {
    text: string;
    canSendMessage: boolean;
    threadId: string;
    onTextChanged: (threadId: string, text: string) => void;
}

export const SendMessageComponent = ({
    text,
    canSendMessage,
    threadId,
    onSend,
    onTextChanged,
}: SendMessageComponentProps) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const aiMessengerEnabled = useFeatureFlag(FeatureFlag.aiMessengerEnabled);
    const [suggestions, setSuggestions] = useState<string>('');
    const [localText, setLocalText] = useState<string>(text);
    const enterpriseProfileId = useCurrentProfileId();
    const messages: Message[] = useCommanderSelector((state) =>
        currentThreadMessages(state, threadId)
    ) as unknown as Message[];
    const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;

    const showAISuggestions = lastMessage && lastMessage.creatorProfileId !== enterpriseProfileId;

    useEffect(() => {
        focus();
    }, [threadId, lastMessage, enterpriseProfileId]);

    useEffect(() => {
        setLocalText(text);
    }, [text]);

    const focus = () => {
        if (textareaRef.current) {
            textareaRef.current.focus();
        }
    };

    const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (e.target.value === text) {
            return;
        }
        const newText = e.target.value.trimStart();
        setLocalText(newText);
        onTextChanged(threadId, newText);
    };

    const handleSend = () => {
        if (canSendMessage) {
            onSend();
            focus();
            setSuggestions('');
        }
    };

    const handleKeyPress = (event: KeyboardEvent) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            if (canSendMessage) {
                handleSend();
            }
        }
    };

    const handleSelectedSuggestion = (suggestion: string) => {
        setSuggestions(suggestion);
        onTextChanged(threadId, suggestion);
    };

    return (
        <MessengerFooter>
            {aiMessengerEnabled ? (
                <Stack vertical spacing="25">
                    {showAISuggestions && (
                        <AiSuggestions
                            lastMessage={lastMessage}
                            handleSelectedSuggestion={handleSelectedSuggestion}
                            text={text}
                        />
                    )}
                    <ChatTextarea
                        ref={textareaRef}
                        disabled={!canSendMessage}
                        maxHeight={256}
                        rows={4}
                        placeholder={lt('AIMessengerPlaceholder')}
                        onChange={handleTextChange}
                        // @ts-expect-error $TSFixMeError: Type '(event: $TSFixMe) => void' is not assignable to type '() => void'.
                        onKeyPress={handleKeyPress}
                        onSubmit={handleSend}
                        value={localText}
                        footerMessage={suggestions && lt('AIMessengerDisclaimer')}
                        iconAccessibleText={lt('AssistantChatIconAccessibleText')}
                        newLayout
                    />
                </Stack>
            ) : (
                <ChatTextarea
                    ref={textareaRef}
                    disabled={!canSendMessage}
                    maxHeight={256}
                    onChange={handleTextChange}
                    // @ts-expect-error $TSFixMeError: Type '(event: $TSFixMe) => void' is not assignable to type '() => void'.
                    onKeyPress={handleKeyPress}
                    onSubmit={handleSend}
                    value={localText}
                />
            )}
        </MessengerFooter>
    );
};
